import { useEffect, useState } from 'react';

export interface ServerStatus {
    /** null while loading, false when the probes fail (e.g. Vite dev without the server). */
    online: boolean | null;
    version?: string;
    uptimeSec?: number;
}

/** Probes the server's own /health endpoint so the page reports real state, not a hard-coded "Active". */
export function useServerStatus(): ServerStatus {
    const [status, setStatus] = useState<ServerStatus>({ online: null });

    useEffect(() => {
        let cancelled = false;
        const origin = window.location.origin;
        const load = async () => {
            try {
                const res = await fetch(`${origin}/health`, { cache: 'no-store' });
                const health = res.ok ? await res.json() : null;
                if (cancelled) return;
                if (!health || health.status !== 'ok') {
                    setStatus({ online: false });
                    return;
                }
                setStatus({
                    online: true,
                    version: typeof health.version === 'string' ? health.version : undefined,
                    uptimeSec: typeof health.uptime === 'number' ? health.uptime : undefined
                });
            } catch {
                if (!cancelled) setStatus({ online: false });
            }
        };
        void load();
        const timer = setInterval(load, 60_000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, []);

    return status;
}

export function formatUptime(sec: number | undefined): string {
    if (sec === undefined) return '';
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}
